import { motion } from "framer-motion";
import { MapPin, MessageCircle } from "lucide-react";
import { WA_LINK } from "@/lib/contact";

const EASE = [0.16, 1, 0.3, 1];

const WORKS = [
  {
    img: "/assets/gallery-1.jpg",
    title: "Full Home Deep Clean",
    place: "Angamaly",
    tag: "Exterior",
  },
  {
    img: "/assets/gallery-2.jpg",
    title: "Team PK On Site",
    place: "Chalakudy",
    tag: "On Site",
  },
  {
    img: "/assets/gallery-3.jpg",
    title: "Careful Furniture Protection",
    place: "Angamaly",
    tag: "Interior",
  },
  {
    img: "/assets/gallery-4.jpg",
    title: "Kitchen Deep Clean",
    place: "Chalakudy",
    tag: "Kitchen",
  },
  {
    img: "/assets/gallery-5.jpg",
    title: "Living Room Refresh",
    place: "Perumbavoor",
    tag: "Interior",
  },
  {
    img: "/assets/gallery-6.jpg",
    title: "Villa Exterior Wash",
    place: "Angamaly",
    tag: "Exterior",
  },
];

export default function Gallery() {
  return (
    <section id="gallery" className="relative py-24 lg:py-36 bg-[#0F1A1C] overflow-hidden grain" data-testid="gallery-section">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(108,190,69,0.18),transparent_65%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14 lg:mb-20">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: EASE }}
              className="font-mono-accent text-xs font-semibold uppercase tracking-[0.28em] text-[#82CB7E] mb-5"
            >
              Before & After Portfolio
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] text-white max-w-2xl"
            >
              The proof is in the{" "}
              <span className="font-serif-accent italic font-semibold text-[#D4AF37]">
                shine
              </span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="text-white/60 max-w-sm text-sm sm:text-base leading-relaxed"
          >
            Real photos from recent PK Hygiene projects — kitchens, bathrooms
            and exterior paving across Angamaly and beyond.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {WORKS.map((w, i) => (
            <motion.figure
              key={w.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.12, ease: EASE }}
              className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 transition-colors duration-500"
              data-testid={`work-item-${i + 1}`}
            >
              <img
                src={w.img}
                alt={`${w.title} — ${w.place}`}
                loading="lazy"
                className="w-full h-72 sm:h-80 object-cover group-hover:scale-[1.06] transition-transform duration-[1200ms] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A1C]/90 via-[#0F1A1C]/10 to-transparent" />
              <span className="absolute top-4 left-4 rounded-full bg-[#6CBE45]/90 backdrop-blur text-white text-[10px] font-mono-accent font-semibold uppercase tracking-[0.18em] px-3 py-1.5">
                {w.tag}
              </span>
              <figcaption className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-white font-semibold text-lg leading-snug">
                  {w.title}
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-[#82CB7E] text-xs font-medium">
                  <MapPin className="w-3.5 h-3.5" />
                  {w.place}
                </p>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-14 text-center"
        >
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="work-whatsapp-cta"
            className="inline-flex items-center gap-3 rounded-full border border-[#25D366]/60 text-[#25D366] font-semibold px-8 py-4 hover:bg-[#25D366] hover:text-white transition-colors duration-300"
          >
            <MessageCircle className="w-5 h-5" />
            Want results like these? Message us
          </a>
        </motion.div>
      </div>
    </section>
  );
}
